import {Injectable} from '@angular/core';
import {environment} from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable} from 'rxjs';
import {ReturnMessageDTO} from '../models/ReturnMessageDTO';
import * as JsonToXML from 'js2xmlparser';
import {Request} from '../models/claim/Request';
import {PagedRequestDTO} from '../../common/models/PagedRequestDTO';
import {PagedResponseDTO} from '../../common/models/PagedResponseDTO';
import {ClaimDisplayDTO} from '../models/claim/ClaimDisplayDTO';
import {WorkflowResultDTO} from '../models/WorkflowResultDTO';
import {NameValueDTO} from '../../common/models/NameValueDTO';
import {FilterPayeesDTO} from '../models/FilterPayeesDTO';
import { ClaimMedschemeResponseDTO } from '../models/claim/ClaimMedschemeResponseDTO';
import { ClaimPhaResponseDTO } from '../models/claim/ClaimPhaResponseDTO';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/xml'
  })
};

@Injectable({
  providedIn: 'root'
})
export class ClaimService {

  private workflowUrl = environment.baseWeUrl + '/claim';
  private claimUrl = environment.baseClmUrl + '/claim';
  private submitAuthToIgn = environment.submitAuthToIgn;
  private submitClaimToIgn = environment.submitClaimToIgn;
  private ignUrl = environment.baseIgnUrl;

  constructor(private httpClient: HttpClient) {
  }

  convertXML(obj, rootElement: string = 'Request') {
    const options = {
      format: {
        doubleQuotes: true
      },
      declaration: {
        include: false
      }
    };

    return JsonToXML.parse(rootElement, obj, options);
  }

  submitClaim(request: Request, isClaim: boolean): Observable<ReturnMessageDTO> {
    // Use IGN integration endpoints when enabled for the request type, otherwise use the normal workflow endpoint
    if (this.submitAuthToIgn && !isClaim) {
      const ignUrlAuthorisation = this.ignUrl + '/authorisation/process-internal';
      return this.httpClient.post<ReturnMessageDTO>(ignUrlAuthorisation, this.convertXML(request), httpOptions);
    } else if (this.submitClaimToIgn && isClaim) {
      const ignUrlClaim = this.ignUrl + '/claim/process-internal';
      return this.httpClient.post<ReturnMessageDTO>(ignUrlClaim, this.convertXML(request), httpOptions);
    }
    // Otherwise use normal endpoint
    else {
      return this.httpClient.post<ReturnMessageDTO>(this.workflowUrl + '/internal', this.convertXML(request), httpOptions);
    }
  }

  submitImportBatchClaim(requests: Request[]): Observable<any> {
    const data = {Request: requests};
    return this.httpClient.post(this.workflowUrl + '/external-list', this.convertXML(data, 'Batch'), {
      headers: new HttpHeaders({
        Accept: 'application/xml',
        'Content-Type': 'application/xml',
      }),
      responseType: 'text'
    });
  }

  getListOfClaims(pagedRequestDTO: PagedRequestDTO): Observable<PagedResponseDTO<ClaimDisplayDTO>> {
    return this.httpClient.post<PagedResponseDTO<ClaimDisplayDTO>>(this.claimUrl + '/filter', pagedRequestDTO);
  }

  // Info: Same as Above just to give different name for better context:

  getListOfTransactions(pagedRequestDTO: PagedRequestDTO, productCode: string): Observable<ClaimMedschemeResponseDTO | ClaimPhaResponseDTO> {
     const parameters = `?productCode=${productCode}`;
    return this.httpClient.post<ClaimMedschemeResponseDTO | ClaimPhaResponseDTO>(this.ignUrl + '/claim/filter'+ parameters, pagedRequestDTO);
  }

  updateActivityStatus(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/update-activity-status', workflowResultDTO);
  }

  updateActivityStatusList(workflowResultDTO: WorkflowResultDTO[]): Observable<WorkflowResultDTO[]> {
    return this.httpClient.post<WorkflowResultDTO[]>(this.claimUrl + '/update-activity-status/list', workflowResultDTO);
  }

  updateTransactionStatus(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/update-transaction-status', workflowResultDTO);
  }

  revertPaidClaim(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/revert-paid-claim', workflowResultDTO);
  }

  markClaimAsPaid(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/mark-claim-paid', workflowResultDTO);
  }

  lockClaim(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/lock-claim', workflowResultDTO);
  }

  unlockClaim(workflowResultDTO: WorkflowResultDTO) {
    return this.httpClient.post<WorkflowResultDTO>(this.claimUrl + '/unlock-claim', workflowResultDTO);
  }

  getPayeesWithClaims(filterPayeesDTO: FilterPayeesDTO): Observable<NameValueDTO[]> {
    return this.httpClient.post<NameValueDTO[]>(this.claimUrl + '/payees', filterPayeesDTO);
  }

  reverseClaim(transactionId: number, createdBy: string): Observable<any> {
    const parameters = `?transactionId=${transactionId}&createdBy=${createdBy}`;
    if (this.submitAuthToIgn) {
      return this.httpClient.post<null>(this.ignUrl + '/claim/reverse' + parameters, null);
    }
    else {
      return this.httpClient.post<null>(this.claimUrl + '/reverse' + parameters, null);
    }
  }

  getConsultationVisits(personId: number): Observable<number> {
    return this.httpClient.get<number>(this.claimUrl + '/consultation-visit?personId=' + personId);
  }

  hasOpenAuthorisations(personId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(this.claimUrl + '/open-authorisations/exists?personId=' + personId);
  }
}
